import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { OracleReveal } from "@/components/runboom/oracle-reveal";
import { heroPrediction, upcomingRaces } from "@/lib/mock";
import { formatRaceTime } from "@/lib/format";

export const Route = createFileRoute("/races/$slug")({
  loader: ({ params }) => {
    const race = upcomingRaces.find((r) => r.slug === params.slug);
    if (!race) throw notFound();
    const prediction =
      params.slug === heroPrediction.raceSlug
        ? heroPrediction
        : {
            raceSlug: race.slug,
            raceName: race.name,
            distanceLabel: race.distanceLabel,
            p10Seconds: Math.max(0, race.yourPredictedSeconds - 85),
            p50Seconds: race.yourPredictedSeconds,
            p90Seconds: race.yourPredictedSeconds + 105,
            boomProbabilityPct: 58,
            drivers: [
              { label: "Fitness trend +4%", deltaSeconds: -22 },
              { label: `Course +${race.elevationM}m gain`, deltaSeconds: Math.min(60, Math.round(race.elevationM / 12) + 4) },
              { label: "Forecast 18°C / 62% RH", deltaSeconds: 15 },
              { label: "Recent 10K PB carryover", deltaSeconds: -18 },
            ],
          };
    return { race, prediction };
  },
  component: RaceDetail,
});

function RaceDetail() {
  const { race, prediction } = Route.useLoaderData();
  const date = new Date(race.date);

  return (
    <main className="mx-auto max-w-[1280px] px-5 py-6 sm:px-6 sm:py-10">
      <nav className="flex items-center gap-3 font-sans text-[13px] text-fg-muted">
        <Link to="/" className="hover:text-fg">RUNBOOM</Link>
        <span aria-hidden>/</span>
        <Link to="/races" className="hover:text-fg">Races</Link>
        <span aria-hidden>/</span>
        <span className="text-fg">{race.name}</span>
      </nav>

      <header className="mt-8 flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="font-sans text-[11px] uppercase tracking-[0.08em] text-fg-subtle">
            {race.city}, {race.country} · {date.toLocaleDateString("en-GB", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </div>
          <h1 className="mt-1 font-sans text-[32px] leading-[36px] tracking-[-0.02em] text-fg sm:text-[56px] sm:leading-[60px] sm:tracking-[-0.025em]">
            {race.name}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Tag>{race.distanceLabel}</Tag>
            <Tag>{race.terrain[0]!.toUpperCase() + race.terrain.slice(1)}</Tag>
            <Tag>+{race.elevationM}m</Tag>
          </div>
        </div>
        {race.entryUrl ? (
          <a
            href={race.entryUrl}
            target="_blank"
            rel="noreferrer"
            className="rounded-[8px] border border-accent bg-accent px-5 py-3 font-mono text-[12px] uppercase tracking-[0.1em] text-night transition-transform active:scale-[0.96]"
          >
            Enter race
          </a>
        ) : null}
      </header>

      <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-[1.4fr_1fr]">
        <OracleReveal prediction={prediction} />

        <aside className="rounded-[12px] border border-border bg-bg-elevated p-6">
          <div className="font-sans text-[11px] uppercase tracking-[0.08em] text-fg-subtle">
            From your crew
          </div>
          <div className="mt-4 flex -space-x-3">
            {race.friendsGoing.map((f) => (
              <Link
                key={f.handle}
                to="/u/$handle"
                params={{ handle: f.handle }}
                className="rounded-full"
              >
                <img
                  src={f.avatar}
                  alt={`@${f.handle}`}
                  className="h-10 w-10 rounded-full border-2 border-bg-elevated object-cover"
                />
              </Link>
            ))}
          </div>
          <div className="mt-4 font-sans text-[14px] text-fg-muted">
            {race.friendsGoing.length} signed up · {Math.max(0, 12 - race.friendsGoing.length)} considering
          </div>

          <div className="mt-8 border-t border-border pt-6">
            <div className="font-sans text-[11px] uppercase tracking-[0.08em] text-fg-subtle">
              Course record
            </div>
            <div className="mt-2 font-mono tabular text-[32px] leading-none text-fg">
              {formatRaceTime(race.courseRecordSeconds)}
            </div>
            <div className="mt-1 font-sans text-[12px] text-fg-subtle">
              All-time, this course
            </div>
          </div>
        </aside>
      </section>

      <footer className="mt-20 border-t border-border pt-6 font-sans text-[12px] text-fg-subtle">
        Every PB is a BOOM · RUNBOOM
      </footer>
    </main>
  );
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-full border border-border px-3 py-1 font-sans text-[12px] uppercase tracking-[0.06em] text-fg-muted">
      {children}
    </span>
  );
}
